import React, { useCallback, useState, memo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useLangFile } from '../../../context/LanguageContext';
import areaCalculation from '../../../algorithms/geometry/AreaCalculation';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import {
  setIsDrawingFieldHole,
  deselectFieldHole,
  removeFieldHole,
} from '../../../reducers/FieldReducer';
import { INNER_BOUNDARIES_HELP_ARTICLE } from '../../../reducers/HelpReducer';
import { setViewMode } from '../../../reducers/ControlReducer';
import ViewModeConstants from '../../../constants/ViewModeConstants';
import HelpItemContainer from '../../Help/HelpItemContainer';
import ConfirmActionPopover from '../../ConfirmPopovers/ConfirmActionPopover';
import { Box, Divider, Typography } from '@mui/material';
import { showProgress, hideProgress } from '../../../reducers/ProgressReducer';
import { useSelectedSeason } from '../../../context/SeasonContext';
import useFirebaseAnalytics, { FIREBASE_EVENTS } from '../../../hooks/useFirebaseAnalytics';
import { clearFieldYieldData } from '../../../reducers/YieldReducer';
import { clearFieldSurveyData } from '../../../reducers/SurveyReducer';

const mapStoreToProps = (store: any) => ({
  selectedField: store.field.selectedField,
  selectedFieldHole: store.field.selectedFieldHole,
  isDrawingFieldHole: store.field.isDrawingFieldHole,
});

type PropsFromRedux = ConnectedProps<typeof connector>;

const InnerBoundariesConfirmActionPopover: React.FC<PropsFromRedux> = ({
  selectedField,
  selectedFieldHole,
  dispatch,
}) => {
  const selectedSeason = useSelectedSeason();
  const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = useState<boolean>(false);
  let status: React.FC | null = null;

  const goToOverviewViewMode: () => void = useCallback(() => {
    dispatch(deselectFieldHole());
    dispatch(setViewMode(ViewModeConstants.EDIT_FIELD));
  }, []);
  const LangFile = useLangFile();
  const analytics = useFirebaseAnalytics();

  const handleAction: () => void = useCallback(() => {
    if (selectedFieldHole) {
      setShowDeleteConfirmationDialog(true);
      return;
    }

    dispatch(setIsDrawingFieldHole(true));
  }, [selectedFieldHole]);

  const handleDelete: () => void = useCallback(() => {
    dispatch(deselectFieldHole());
    dispatch(showProgress());
    dispatch(clearFieldYieldData(selectedField.fieldId));
    dispatch(clearFieldSurveyData(selectedField.fieldId));
    dispatch(
      removeFieldHole(
        selectedField.fieldId,
        selectedSeason.id,
        selectedFieldHole,
        selectedField.polygon.coordinates
      )
    ).then(() => {
      analytics.logEvent(FIREBASE_EVENTS.EF_FIELD_HOLE_REMOVED);

      dispatch(hideProgress());
    });
    setShowDeleteConfirmationDialog(false);
  }, [selectedFieldHole, selectedField.fieldId, selectedField.polygon.coordinates]);

  const handleDeleteConfirmationDialogCancel: () => void = useCallback(() => {
    setShowDeleteConfirmationDialog(false);
  }, []);

  if (selectedFieldHole) {
    const size: string = areaCalculation({
      coords: selectedFieldHole.map((coord: [number, number]) => ({
        lng: coord[0],
        lat: coord[1],
      })),
    }).size.toFixed(2);
    status = (
      <>
        <Box>
          <Divider style={{ marginTop: '0', marginBottom: '8px', backgroundColor: '#ddd' }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant={'body2'}>
              {LangFile.PolygonDrawing.drawnInnerBoundaryArea + ':'}
            </Typography>
            <Typography variant={'body2'} style={{ fontWeight: 'bold', color: '#2c3e50' }}>
              {size} ha
            </Typography>
          </Box>
        </Box>
      </>
    );
  }

  const helpIcon: React.FC = (
    <HelpItemContainer type={'icon'} articleId={INNER_BOUNDARIES_HELP_ARTICLE} />
  );

  return (
    <>
      <ConfirmActionPopover
        shown={true}
        status={status}
        LangFile={LangFile}
        action={handleAction}
        actionText={
          selectedFieldHole
            ? LangFile.AddInnerBoundariesConfirmationPopover.deleteText
            : LangFile.AddInnerBoundariesConfirmationPopover.addText
        }
        actionButtonColor={selectedFieldHole ? 'secondary' : 'primary'}
        cancelAction={goToOverviewViewMode}
        cancelText={LangFile.AddInnerBoundariesConfirmationPopover.cancelText}
        title={LangFile.AddInnerBoundariesConfirmationPopover.title}
        message={
          selectedFieldHole
            ? LangFile.AddInnerBoundariesConfirmationPopover.selectedBoundaryMessage
            : LangFile.AddInnerBoundariesConfirmationPopover.message
        }
        disableContentPadding={selectedFieldHole == null}
        disabled={false}
        disabledReason={''}
        style={{ marginTop: '30px' }}
        helpIcon={helpIcon}
      />
      {showDeleteConfirmationDialog && (
        <ConfirmationDialog
          title={LangFile.EditFieldDeleteInnerBoundary.title}
          content={
            <Typography variant="body1">
              <strong>{LangFile.EditFieldDeleteInnerBoundary.note}: </strong>
              {LangFile.EditFieldDeleteInnerBoundary.content}
            </Typography>
          }
          onCancel={handleDeleteConfirmationDialogCancel}
          onDelete={handleDelete}
          open={true}
        />
      )}
    </>
  );
};

const connector = connect(mapStoreToProps);
export default memo(connector(InnerBoundariesConfirmActionPopover));
